import {
  Box,
  Heading,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  SimpleGrid,
  Icon,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import { useAppContext } from "../contexts/app";
import { useIsUserInSandbox } from "../shared/useIsUserInSandbox";
import { FiUsers, FiActivity, FiAlertTriangle, FiHeart } from "react-icons/fi";
import { FaRegHospital } from "react-icons/fa";
import { useEffect, useState } from "react";
import Chart from "react-apexcharts";

interface PopulationStats {
  totalPatients: number;
  activePatients: number;
  averageAge: number;
  genderDistribution: {
    male: number;
    female: number;
    other: number;
  };
  hospitalizationsPerMonth: Array<{
    month: string;
    count: number;
  }>;
  readmissionRate: number;
  highRiskPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastEDVisit: string;
    reasonForVisit: string;
    riskFactors: string[];
  }>;
  highRiskPatientsPerMonth: Array<{
    month: string;
    count: number;
  }>;
  patientsWithHighA1c: Array<{
    id: string;
    name: string;
    age: number;
    a1cValue: number;
    lastTestDate: string;
  }>;
  patientsWithHighA1cPerMonth: Array<{
    month: string;
    count: number;
  }>;
  // Add new HEDIS patient data
  breastCancerPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastMammogram: string;
    status: "compliant" | "non-compliant";
  }>;
  colorectalPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastScreening: string;
    screeningType: string;
    status: "compliant" | "non-compliant";
  }>;
  diabetesPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastA1c: string;
    a1cValue: number;
    status: "controlled" | "uncontrolled";
  }>;
  hypertensionPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastBP: string;
    systolic: number;
    diastolic: number;
    status: "controlled" | "uncontrolled";
  }>;
  immunizationPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastImmunization: string;
    missingVaccines: string[];
    status: "complete" | "incomplete";
  }>;
  depressionPatients: Array<{
    id: string;
    name: string;
    age: number;
    lastScreening: string;
    screeningScore: number;
    status: "screened" | "not-screened";
  }>;
  statinPatients: Array<{
    id: string;
    name: string;
    age: number;
    diagnosis: string;
    lastPrescription: string;
    status: "prescribed" | "not-prescribed";
  }>;
  tobaccoPatients: Array<{
    id: string;
    name: string;
    age: number;
    tobaccoUse: string;
    lastIntervention: string;
    status: "intervention" | "no-intervention";
  }>;
}

export default function PopulationHealth() {
  const { state } = useAppContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);
  const [stats, setStats] = useState<PopulationStats | null>(null);
  const [loading, setLoading] = useState(true);

  const headerTextColor = useColorModeValue("gray.700", "gray.200");
  const hoverBgColor = useColorModeValue("blue.50", "blue.900");
  const primaryTextColor = useColorModeValue("gray.800", "white");
  const secondaryTextColor = useColorModeValue("gray.600", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.700");

  // Check if user is a Metriport employee
  const isMetriportUser = state.user?.email?.endsWith("@metriport.com");

  useEffect(() => {
    const fetchPopulationStats = async () => {
      try {
        setLoading(true);
        // Mock data for now - in a real implementation, you would call the API
        const mockStats: PopulationStats = {
          totalPatients: 1247,
          activePatients: 892,
          averageAge: 42.3,
          genderDistribution: {
            male: 48,
            female: 51,
            other: 1,
          },
          hospitalizationsPerMonth: [
            { month: "Jan 2024", count: 45 },
            { month: "Feb 2024", count: 52 },
            { month: "Mar 2024", count: 38 },
            { month: "Apr 2024", count: 61 },
            { month: "May 2024", count: 49 },
            { month: "Jun 2024", count: 55 },
          ],
          readmissionRate: 12.3,
          highRiskPatients: [
            {
              id: "P001",
              name: "John Smith",
              age: 67,
              lastEDVisit: "2024-06-15",
              reasonForVisit: "Chest pain and shortness of breath",
              riskFactors: ["Diabetes", "Hypertension", "Recent ED Visit"],
            },
            {
              id: "P002",
              name: "Mary Johnson",
              age: 72,
              lastEDVisit: "2024-06-12",
              reasonForVisit: "Severe headache and confusion",
              riskFactors: ["Heart Disease", "COPD"],
            },
            {
              id: "P003",
              name: "Robert Davis",
              age: 58,
              lastEDVisit: "2024-06-10",
              reasonForVisit: "Uncontrolled blood sugar levels",
              riskFactors: ["Diabetes", "Kidney Disease"],
            },
            {
              id: "P004",
              name: "Sarah Wilson",
              age: 65,
              lastEDVisit: "2024-06-08",
              reasonForVisit: "Dizziness and balance issues",
              riskFactors: ["Hypertension", "Stroke History"],
            },
            {
              id: "P005",
              name: "Michael Brown",
              age: 71,
              lastEDVisit: "2024-06-05",
              reasonForVisit: "Irregular heartbeat and fatigue",
              riskFactors: ["Heart Disease", "Diabetes"],
            },
          ],
          highRiskPatientsPerMonth: [
            { month: "Jan 2024", count: 23 },
            { month: "Feb 2024", count: 28 },
            { month: "Mar 2024", count: 31 },
            { month: "Apr 2024", count: 26 },
            { month: "May 2024", count: 34 },
            { month: "Jun 2024", count: 29 },
          ],
          patientsWithHighA1c: [
            {
              id: "P006",
              name: "Jennifer Lee",
              age: 45,
              a1cValue: 9.8,
              lastTestDate: "2024-06-10",
            },
            {
              id: "P007",
              name: "David Miller",
              age: 52,
              a1cValue: 10.2,
              lastTestDate: "2024-06-08",
            },
            { id: "P008", name: "Lisa Garcia", age: 38, a1cValue: 9.5, lastTestDate: "2024-06-12" },
            {
              id: "P009",
              name: "James Taylor",
              age: 61,
              a1cValue: 9.9,
              lastTestDate: "2024-06-05",
            },
            {
              id: "P010",
              name: "Patricia Anderson",
              age: 49,
              a1cValue: 10.1,
              lastTestDate: "2024-06-15",
            },
          ],
          patientsWithHighA1cPerMonth: [
            { month: "Jan 2024", count: 15 },
            { month: "Feb 2024", count: 18 },
            { month: "Mar 2024", count: 22 },
            { month: "Apr 2024", count: 19 },
            { month: "May 2024", count: 25 },
            { month: "Jun 2024", count: 21 },
          ],
          // Add HEDIS patient data
          breastCancerPatients: [
            {
              id: "BC001",
              name: "Alice Johnson",
              age: 58,
              lastMammogram: "2024-05-15",
              status: "compliant",
            },
            {
              id: "BC002",
              name: "Betty Smith",
              age: 62,
              lastMammogram: "2023-11-20",
              status: "non-compliant",
            },
            {
              id: "BC003",
              name: "Carol Davis",
              age: 55,
              lastMammogram: "2024-06-01",
              status: "compliant",
            },
            {
              id: "BC004",
              name: "Diana Wilson",
              age: 67,
              lastMammogram: "2023-08-10",
              status: "non-compliant",
            },
            {
              id: "BC005",
              name: "Eva Brown",
              age: 59,
              lastMammogram: "2024-04-22",
              status: "compliant",
            },
          ],
          colorectalPatients: [
            {
              id: "CR001",
              name: "Frank Miller",
              age: 58,
              lastScreening: "2024-03-15",
              screeningType: "Colonoscopy",
              status: "compliant",
            },
            {
              id: "CR002",
              name: "George Taylor",
              age: 64,
              lastScreening: "2023-09-10",
              screeningType: "FIT",
              status: "non-compliant",
            },
            {
              id: "CR003",
              name: "Henry Anderson",
              age: 61,
              lastScreening: "2024-05-20",
              screeningType: "Colonoscopy",
              status: "compliant",
            },
            {
              id: "CR004",
              name: "Ivan Garcia",
              age: 69,
              lastScreening: "2022-12-05",
              screeningType: "Cologuard",
              status: "non-compliant",
            },
            {
              id: "CR005",
              name: "Jack Lee",
              age: 56,
              lastScreening: "2024-06-12",
              screeningType: "FIT",
              status: "compliant",
            },
          ],
          diabetesPatients: [
            {
              id: "DM001",
              name: "Karen White",
              age: 52,
              lastA1c: "2024-06-10",
              a1cValue: 7.2,
              status: "controlled",
            },
            {
              id: "DM002",
              name: "Linda Clark",
              age: 48,
              lastA1c: "2024-05-28",
              a1cValue: 8.5,
              status: "uncontrolled",
            },
            {
              id: "DM003",
              name: "Maria Rodriguez",
              age: 61,
              lastA1c: "2024-06-15",
              a1cValue: 6.8,
              status: "controlled",
            },
            {
              id: "DM004",
              name: "Nancy Martinez",
              age: 55,
              lastA1c: "2024-05-20",
              a1cValue: 9.1,
              status: "uncontrolled",
            },
            {
              id: "DM005",
              name: "Olivia Thompson",
              age: 49,
              lastA1c: "2024-06-08",
              a1cValue: 7.5,
              status: "controlled",
            },
          ],
          hypertensionPatients: [
            {
              id: "HT001",
              name: "Paul Walker",
              age: 58,
              lastBP: "2024-06-12",
              systolic: 135,
              diastolic: 85,
              status: "controlled",
            },
            {
              id: "HT002",
              name: "Quinn Young",
              age: 62,
              lastBP: "2024-05-30",
              systolic: 145,
              diastolic: 95,
              status: "uncontrolled",
            },
            {
              id: "HT003",
              name: "Rachel Hall",
              age: 55,
              lastBP: "2024-06-18",
              systolic: 128,
              diastolic: 82,
              status: "controlled",
            },
            {
              id: "HT004",
              name: "Sam Allen",
              age: 67,
              lastBP: "2024-06-05",
              systolic: 150,
              diastolic: 92,
              status: "uncontrolled",
            },
            {
              id: "HT005",
              name: "Tina King",
              age: 59,
              lastBP: "2024-06-20",
              systolic: 132,
              diastolic: 88,
              status: "controlled",
            },
          ],
          immunizationPatients: [
            {
              id: "IM001",
              name: "Uma Patel",
              age: 2,
              lastImmunization: "2024-06-15",
              missingVaccines: [],
              status: "complete",
            },
            {
              id: "IM002",
              name: "Victor Singh",
              age: 2,
              lastImmunization: "2024-05-20",
              missingVaccines: ["MMR", "Varicella"],
              status: "incomplete",
            },
            {
              id: "IM003",
              name: "Wendy Chen",
              age: 2,
              lastImmunization: "2024-06-10",
              missingVaccines: [],
              status: "complete",
            },
            {
              id: "IM004",
              name: "Xavier Kim",
              age: 2,
              lastImmunization: "2024-04-25",
              missingVaccines: ["DTaP", "Hepatitis B"],
              status: "incomplete",
            },
            {
              id: "IM005",
              name: "Yara Ali",
              age: 2,
              lastImmunization: "2024-06-18",
              missingVaccines: [],
              status: "complete",
            },
          ],
          depressionPatients: [
            {
              id: "DP001",
              name: "Zoe Adams",
              age: 16,
              lastScreening: "2024-06-10",
              screeningScore: 8,
              status: "screened",
            },
            {
              id: "DP002",
              name: "Adam Baker",
              age: 25,
              lastScreening: "2024-05-15",
              screeningScore: 12,
              status: "screened",
            },
            {
              id: "DP003",
              name: "Bella Cooper",
              age: 19,
              lastScreening: "2023-12-20",
              screeningScore: 0,
              status: "not-screened",
            },
            {
              id: "DP004",
              name: "Charlie Evans",
              age: 32,
              lastScreening: "2024-06-05",
              screeningScore: 6,
              status: "screened",
            },
            {
              id: "DP005",
              name: "Daisy Foster",
              age: 28,
              lastScreening: "2024-04-30",
              screeningScore: 0,
              status: "not-screened",
            },
          ],
          statinPatients: [
            {
              id: "ST001",
              name: "Ethan Green",
              age: 58,
              diagnosis: "Coronary Artery Disease",
              lastPrescription: "2024-06-01",
              status: "prescribed",
            },
            {
              id: "ST002",
              name: "Fiona Hill",
              age: 64,
              diagnosis: "Myocardial Infarction",
              lastPrescription: "2024-05-20",
              status: "prescribed",
            },
            {
              id: "ST003",
              name: "Gavin Irwin",
              age: 61,
              diagnosis: "Stroke",
              lastPrescription: "2023-11-15",
              status: "not-prescribed",
            },
            {
              id: "ST004",
              name: "Holly Jones",
              age: 55,
              diagnosis: "Peripheral Artery Disease",
              lastPrescription: "2024-06-10",
              status: "prescribed",
            },
            {
              id: "ST005",
              name: "Ian Kelly",
              age: 67,
              diagnosis: "Coronary Artery Disease",
              lastPrescription: "2024-04-25",
              status: "prescribed",
            },
          ],
          tobaccoPatients: [
            {
              id: "TB001",
              name: "Julia Lewis",
              age: 45,
              tobaccoUse: "Cigarettes",
              lastIntervention: "2024-06-12",
              status: "intervention",
            },
            {
              id: "TB002",
              name: "Kevin Moore",
              age: 52,
              tobaccoUse: "Cigarettes",
              lastIntervention: "2024-05-08",
              status: "intervention",
            },
            {
              id: "TB003",
              name: "Luna Nelson",
              age: 38,
              tobaccoUse: "E-cigarettes",
              lastIntervention: "2023-12-15",
              status: "no-intervention",
            },
            {
              id: "TB004",
              name: "Mason Owen",
              age: 49,
              tobaccoUse: "Cigarettes",
              lastIntervention: "2024-06-05",
              status: "intervention",
            },
            {
              id: "TB005",
              name: "Nova Phillips",
              age: 41,
              tobaccoUse: "Cigars",
              lastIntervention: "2024-04-20",
              status: "no-intervention",
            },
          ],
        };

        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        setStats(mockStats);
      } catch (error) {
        console.error("Error fetching population stats:", error);
      } finally {
        setLoading(false);
      }
    };

    if (isMetriportUser) {
      fetchPopulationStats();
    }
  }, [isMetriportUser]);

  if (!isMetriportUser) {
    return (
      <Box p={10} textAlign="center">
        <Heading size="lg" mb={4}>
          Access Restricted
        </Heading>
        <Text>This page is only available to Metriport employees.</Text>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box p={10}>
        <Heading size="lg" mb={6}>
          Population Health Dashboard
        </Heading>
        <Text>Loading population health data...</Text>
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box p={10}>
        <Heading size="lg" mb={6}>
          Population Health Dashboard
        </Heading>
        <Text>Unable to load population health data.</Text>
      </Box>
    );
  }

  return (
    <Box p={10}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">Population Analytics</Heading>

        {isUserInSandbox && (
          <Box p={4} bg="blue.50" borderRadius="md" border="1px" borderColor="blue.200">
            <Text fontSize="sm" color="blue.700">
              <Icon as={FiAlertTriangle} mr={2} />
              Sandbox Mode: Displaying sample population health data
            </Text>
          </Box>
        )}

        {/* Key Metrics */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          <Card>
            <CardBody>
              <Stat>
                <StatLabel>
                  <HStack>
                    <Icon as={FiUsers} />
                    <Text>Total Patients</Text>
                  </HStack>
                </StatLabel>
                <StatNumber>{stats.totalPatients.toLocaleString()}</StatNumber>
                <StatHelpText>
                  <StatArrow type="increase" />
                  12.5%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>
                  <HStack>
                    <Icon as={FiActivity} />
                    <Text>30-Day Readmission Rate</Text>
                  </HStack>
                </StatLabel>
                <StatNumber>{stats.readmissionRate}%</StatNumber>
                <StatHelpText>
                  <StatArrow type="decrease" />
                  2.1% from last month
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>
                  <HStack>
                    <Icon as={FaRegHospital} />
                    <Text>Average Age</Text>
                  </HStack>
                </StatLabel>
                <StatNumber>{stats.averageAge}</StatNumber>
                <StatHelpText>Years</StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <Stat>
                <StatLabel>
                  <HStack>
                    <Icon as={FiHeart} />
                    <Text>Chronically Ill Patients</Text>
                  </HStack>
                </StatLabel>
                <StatNumber>34.2%</StatNumber>
                <StatHelpText>of total population</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Charts Section - Three Across */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {/* Hospitalizations per Month */}
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                Hospitalizations per Month
              </Heading>
              <Chart
                options={{
                  chart: {
                    id: "hospitalizations-chart",
                    toolbar: {
                      show: true,
                    },
                  },
                  xaxis: {
                    categories: stats.hospitalizationsPerMonth.map(h => h.month),
                    title: {
                      text: "Month",
                    },
                  },
                  yaxis: {
                    title: {
                      text: "Number of Hospitalizations",
                    },
                    min: 0,
                  },
                  colors: ["#E53E3E"],
                  dataLabels: {
                    enabled: true,
                    style: {
                      colors: ["#E53E3E"],
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: function (val) {
                        return val + " hospitalizations";
                      },
                    },
                  },
                  stroke: {
                    curve: "smooth",
                    width: 3,
                  },
                  markers: {
                    size: 6,
                    colors: ["#E53E3E"],
                    strokeColors: "#fff",
                    strokeWidth: 2,
                  },
                }}
                series={[
                  {
                    name: "Hospitalizations",
                    data: stats.hospitalizationsPerMonth.map(h => h.count),
                  },
                ]}
                type="line"
                height={350}
              />
            </CardBody>
          </Card>

          {/* Patients with A1c >9% per Month */}
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                Patients with A1c &gt;9% per Month
              </Heading>
              <Chart
                options={{
                  chart: {
                    id: "a1c-patients-chart",
                    toolbar: {
                      show: true,
                    },
                  },
                  xaxis: {
                    categories: stats.patientsWithHighA1cPerMonth.map(h => h.month),
                    title: {
                      text: "Month",
                    },
                  },
                  yaxis: {
                    title: {
                      text: "Number of Patients",
                    },
                    min: 0,
                  },
                  colors: ["#E53E3E"],
                  dataLabels: {
                    enabled: true,
                    style: {
                      colors: ["#E53E3E"],
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: function (val) {
                        return val + " patients";
                      },
                    },
                  },
                  stroke: {
                    curve: "smooth",
                    width: 3,
                  },
                  markers: {
                    size: 6,
                    colors: ["#E53E3E"],
                    strokeColors: "#fff",
                    strokeWidth: 2,
                  },
                }}
                series={[
                  {
                    name: "Patients with A1c >9%",
                    data: stats.patientsWithHighA1cPerMonth.map(h => h.count),
                  },
                ]}
                type="line"
                height={350}
              />
            </CardBody>
          </Card>

          {/* High Risk Patients per Month */}
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                High Risk Patients per Month
              </Heading>
              <Chart
                options={{
                  chart: {
                    id: "high-risk-patients-chart",
                    toolbar: {
                      show: true,
                    },
                  },
                  xaxis: {
                    categories: stats.highRiskPatientsPerMonth.map(h => h.month),
                    title: {
                      text: "Month",
                    },
                  },
                  yaxis: {
                    title: {
                      text: "Number of High Risk Patients",
                    },
                    min: 0,
                  },
                  colors: ["#ED8936"],
                  dataLabels: {
                    enabled: true,
                    style: {
                      colors: ["#ED8936"],
                    },
                  },
                  tooltip: {
                    y: {
                      formatter: function (val) {
                        return val + " patients";
                      },
                    },
                  },
                  stroke: {
                    curve: "smooth",
                    width: 3,
                  },
                  markers: {
                    size: 6,
                    colors: ["#ED8936"],
                    strokeColors: "#fff",
                    strokeWidth: 2,
                  },
                }}
                series={[
                  {
                    name: "High Risk Patients",
                    data: stats.highRiskPatientsPerMonth.map(h => h.count),
                  },
                ]}
                type="line"
                height={350}
              />
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Tables Section - Side by Side */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
          {/* High Risk Patients */}
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                High Risk Patients (Recent ED Visit)
              </Heading>
              <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                <Table variant="simple">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Name
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Age
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Last ED Visit
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Reason for Visit
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stats.highRiskPatients.map(patient => (
                      <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                        <Td fontWeight="bold" color={primaryTextColor}>
                          {patient.name}
                        </Td>
                        <Td color={secondaryTextColor}>{patient.age}</Td>
                        <Td color={secondaryTextColor}>{patient.lastEDVisit}</Td>
                        <Td color={secondaryTextColor}>{patient.reasonForVisit}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>

          {/* Patients with A1c >9% */}
          <Card>
            <CardBody>
              <Heading size="md" mb={4}>
                Patients with A1c &gt;9%
              </Heading>
              <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                <Table variant="simple">
                  <Thead bg={tableHeaderBg}>
                    <Tr>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Name
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Age
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        A1c Value
                      </Th>
                      <Th color={headerTextColor} fontWeight="semibold">
                        Last Test Date
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {stats.patientsWithHighA1c.map(patient => (
                      <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                        <Td fontWeight="bold" color={primaryTextColor}>
                          {patient.name}
                        </Td>
                        <Td color={secondaryTextColor}>{patient.age}</Td>
                        <Td color="red.500" fontWeight="bold">
                          {patient.a1cValue}%
                        </Td>
                        <Td color={secondaryTextColor}>{patient.lastTestDate}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Care Gaps (HEDIS) Section */}
        <Box>
          <Heading size="lg" mb={6}>
            Care Gaps (HEDIS)
          </Heading>

          {/* HEDIS Measures - Side by Side Layout */}
          <VStack spacing={8} align="stretch">
            {/* Breast Cancer Screening */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Breast Cancer Screening (BCS)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of women 50-74 years of age who had a mammogram to screen for breast
                    cancer
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "breast-cancer-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Screening Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#3182CE"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Screening Rate",
                        data: [82, 83, 85, 84, 86, 87],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    BCS Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Mammogram
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.breastCancerPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastMammogram}</Td>
                            <Td>
                              <Text
                                color={patient.status === "compliant" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "compliant" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Colorectal Cancer Screening */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Colorectal Cancer Screening (COL)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of adults 50-75 years of age who had appropriate screening for
                    colorectal cancer
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "colorectal-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Screening Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#E53E3E"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Screening Rate",
                        data: [68, 70, 69, 71, 72, 71],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    COL Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Screening
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Type
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.colorectalPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastScreening}</Td>
                            <Td color={secondaryTextColor}>{patient.screeningType}</Td>
                            <Td>
                              <Text
                                color={patient.status === "compliant" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "compliant" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Diabetes HbA1c Control */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Diabetes HbA1c Control (DMC)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of patients 18-75 years with diabetes whose HbA1c level was &lt; 8.0%
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "diabetes-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Control Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#38A169"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Control Rate",
                        data: [64, 66, 65, 67, 68, 66],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    DMC Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last A1c
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            A1c Value
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.diabetesPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastA1c}</Td>
                            <Td
                              color={patient.a1cValue >= 8.0 ? "red.500" : "green.500"}
                              fontWeight="bold"
                            >
                              {patient.a1cValue}%
                            </Td>
                            <Td>
                              <Text
                                color={patient.status === "controlled" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "controlled" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Hypertension Control */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Hypertension Control (HPC)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of patients 18-85 years with hypertension whose blood pressure was
                    &lt; 140/90 mmHg
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "hypertension-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Control Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#D69E2E"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Control Rate",
                        data: [78, 79, 81, 80, 82, 81],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    HPC Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last BP
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            BP Reading
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.hypertensionPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastBP}</Td>
                            <Td
                              color={
                                patient.systolic >= 140 || patient.diastolic >= 90
                                  ? "red.500"
                                  : "green.500"
                              }
                              fontWeight="bold"
                            >
                              {patient.systolic}/{patient.diastolic}
                            </Td>
                            <Td>
                              <Text
                                color={patient.status === "controlled" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "controlled" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Childhood Immunizations */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Childhood Immunizations (CIS)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of children 2 years of age who had the recommended immunizations by
                    their second birthday
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "immunizations-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Immunization Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#805AD5"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Immunization Rate",
                        data: [92, 93, 92, 94, 93, 94],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    CIS Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Immunization
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Missing Vaccines
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.immunizationPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastImmunization}</Td>
                            <Td color={secondaryTextColor}>
                              {patient.missingVaccines.length > 0
                                ? patient.missingVaccines.join(", ")
                                : "None"}
                            </Td>
                            <Td>
                              <Text
                                color={patient.status === "complete" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "complete" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Depression Screening */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Depression Screening (DSM)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of adolescents 12-18 years and adults 19+ years who were screened for
                    clinical depression
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "depression-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Screening Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#319795"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Screening Rate",
                        data: [72, 74, 73, 75, 76, 75],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    DSM Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Screening
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Score
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.depressionPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.lastScreening}</Td>
                            <Td color={secondaryTextColor}>{patient.screeningScore}</Td>
                            <Td>
                              <Text
                                color={patient.status === "screened" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "screened" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Statin Therapy for CVD */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Statin Therapy for CVD (SPC)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of patients 21-75 years with cardiovascular disease who were
                    prescribed statin therapy
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "statin-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Prescription Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#DD6B20"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Prescription Rate",
                        data: [61, 63, 62, 64, 65, 64],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    SPC Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Diagnosis
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Prescription
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.statinPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.diagnosis}</Td>
                            <Td color={secondaryTextColor}>{patient.lastPrescription}</Td>
                            <Td>
                              <Text
                                color={patient.status === "prescribed" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "prescribed" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>

            {/* Tobacco Cessation */}
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              {/* Chart Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    Tobacco Cessation (TSC)
                  </Heading>
                  <Text fontSize="sm" color="gray.600" mb={6}>
                    Percentage of tobacco users who received cessation intervention during the
                    measurement year
                  </Text>
                  <Chart
                    options={{
                      chart: {
                        id: "tobacco-chart",
                        toolbar: { show: false },
                      },
                      xaxis: {
                        categories: ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
                        title: { text: "Month" },
                      },
                      yaxis: {
                        title: { text: "Intervention Rate (%)" },
                        min: 0,
                        max: 100,
                      },
                      colors: ["#E53E3E"],
                      dataLabels: { enabled: true },
                      stroke: { curve: "smooth", width: 3 },
                      markers: { size: 6 },
                    }}
                    series={[
                      {
                        name: "Intervention Rate",
                        data: [54, 56, 55, 57, 58, 57],
                      },
                    ]}
                    type="line"
                    height={300}
                  />
                </CardBody>
              </Card>

              {/* Patient List Card */}
              <Card>
                <CardBody>
                  <Heading size="md" mb={4}>
                    TSC Patient List
                  </Heading>
                  <TableContainer borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Table variant="simple">
                      <Thead bg={tableHeaderBg}>
                        <Tr>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Name
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Age
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Tobacco Use
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Last Intervention
                          </Th>
                          <Th color={headerTextColor} fontWeight="semibold">
                            Status
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stats.tobaccoPatients.map(patient => (
                          <Tr key={patient.id} _hover={{ bg: hoverBgColor }}>
                            <Td fontWeight="bold" color={primaryTextColor}>
                              {patient.name}
                            </Td>
                            <Td color={secondaryTextColor}>{patient.age}</Td>
                            <Td color={secondaryTextColor}>{patient.tobaccoUse}</Td>
                            <Td color={secondaryTextColor}>{patient.lastIntervention}</Td>
                            <Td>
                              <Text
                                color={patient.status === "intervention" ? "green.500" : "red.500"}
                                fontWeight="bold"
                              >
                                {patient.status === "intervention" ? "✓" : "✗"}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </TableContainer>
                </CardBody>
              </Card>
            </SimpleGrid>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
